import React, { useMemo } from "react"

import { useRecoilValue } from "recoil"

import { useExitPrompt } from "~/hooks/useExitPrompt"

import {
  designDiffState,
  designLocalState,
  designRemoteState,
} from "./design-state"
import { useDesignMachinery } from "./use-design"
import type { DesignActions, DesignStatus, UseDesign } from "./use-design"

const DesignActionsContext = React.createContext<DesignActions | null>(null)
const DesignStatusContext = React.createContext<DesignStatus | null>(null)

export const DesignContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { actions, status } = useDesignMachinery()
  const delta = useRecoilValue(designDiffState)

  useExitPrompt(delta !== null || status.isSaving)

  return (
    <DesignActionsContext.Provider value={actions}>
      <DesignStatusContext.Provider value={status}>
        {children}
      </DesignStatusContext.Provider>
    </DesignActionsContext.Provider>
  )
}

/** Supplies design actions from a host that saves without the V1 GraphQL machinery. */
export const DesignActionsProvider: React.FC<{
  actions: DesignActions
  status: DesignStatus
  children: React.ReactNode
}> = ({ actions, status, children }) => (
  <DesignActionsContext.Provider value={actions}>
    <DesignStatusContext.Provider value={status}>
      {children}
    </DesignStatusContext.Provider>
  </DesignActionsContext.Provider>
)

const rejectPublicEdit = (): never => {
  throw new Error(`Public presentations are read-only`)
}
const readOnlyDesignActions: DesignActions = {
  setLocal: rejectPublicEdit,
  changeLocal: rejectPublicEdit,
  save: rejectPublicEdit,
  reset: rejectPublicEdit,
  resetLocal: rejectPublicEdit,
  resetRemote: rejectPublicEdit,
}
const readOnlyDesignStatus: DesignStatus = {
  isSaving: false,
  isResetting: false,
  isSuccess: true,
}
export const ReadOnlyDesignContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => (
  <DesignActionsProvider
    actions={readOnlyDesignActions}
    status={readOnlyDesignStatus}
  >
    {children}
  </DesignActionsProvider>
)

export const useDesignActions = (): DesignActions => {
  const actions = React.useContext(DesignActionsContext)

  if (!actions) {
    throw new Error(`Design context not found`)
  }

  return actions
}

export const useDesignStatus = (): DesignStatus => {
  const status = React.useContext(DesignStatusContext)

  if (!status) {
    throw new Error(`Design context not found`)
  }

  return status
}

export const useDesignContext = (): ReturnType<UseDesign> => {
  const actions = useDesignActions()
  const status = useDesignStatus()
  const local = useRecoilValue(designLocalState)
  const remote = useRecoilValue(designRemoteState)
  const delta = useRecoilValue(designDiffState)

  return useMemo(
    () => ({
      ...actions,
      ...status,
      local,
      remote,
      delta,
      isDirty: delta !== null,
    }),
    [actions, status, local, remote, delta],
  )
}
